/**
 *
 *  Header for Become a Coach page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 22/05/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { Link, NavLink } from "react-router-dom";
import { FormattedMessage } from "react-intl";

// Components
import Button from "../../Button/Button";
import Menu from "../../Header/Menu/Menu";
import NavMenu from "../../Header/NavMenu/NavMenu";
import { MenuItem, ButtonElement } from "../../Header/Header";
import { NavMenuLink } from "../../Header/NavMenu/NavMenu";

// Contexts
import { useRegion } from "../../../context/RegionProvider";

// Icons
import UserPicOIcon from "../../../icons/UserPicOIcon";

// Utils
import { clientWidth } from "../../../utils";

import TruBeLogo from "../../Header/TruBeLogo";
import Globe from "../../Header/Globe";

const Header = ({
  behaviour,
  isLoggedIn,
  showLinks,
  showMenu,
  showRegionSelect,
  returnTo,
  // region = "GB",
  heroRef,
  onFaqClick,
  onChooseRegionClick,
}) => {
  const { region, openModal } = useRegion();
  const [inverse, setInverse] = useState(behaviour !== "none");
  const [atTop, setAtTop] = useState(true);

  const inverseRef = useRef(inverse);

  const handleScroll = useCallback(() => {
    if (!heroRef) {
      return;
    }
    var heroEl = ReactDOM.findDOMNode(heroRef.current);
    var distanceTop = heroEl.offsetTop + heroEl.offsetHeight;

    if (behaviour === "none") {
      return;
    }

    if (window.pageYOffset === 0) {
      setAtTop(true);
    } else {
      setAtTop(false);
    }

    if (window.pageYOffset > distanceTop) {
      if (inverseRef.current) {
        setInverse(false);
        inverseRef.current = false;
      }
    } else if (!inverseRef.current) {
      setInverse(true);
      inverseRef.current = true;
    }
  }, [heroRef, behaviour]);

  useEffect(() => {
    document.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  });

  const handleRegionClick = () => {
    openModal();
    onChooseRegionClick && onChooseRegionClick();
  };

  return (
    <nav
      role="navigation"
      className={`nav nav-top tbk-fixed tbk-left-0 tbk-right-0 tbk-h-7 tbk-border-b ${
        inverse
          ? "tbk-border-transparent" +
            (atTop ? " tbk-border-none" : " with-blur _tbk-backdrop-blur-xl")
          : "tbk-border-solid tbk-border-blue-grey-light tbk-bg-basic-white"
      } tbk-z-995 tbk-will-change-background`}
    >
      <div className="tbk-container tbk-mx-auto tbk-h-full tbk-px-2">
        <div className="tbk-relative tbk-flex tbk-h-full tbk-items-center tbk-py-1">
          <div className="tbk-mr-0 md:tbk-mr-7">
            <div className="tb-logo tbk-ml-0 tbk-max-w-[165px]">
              <a
                className="tb-logo-link tbk-text-primary"
                href={`/${region === "US" ? "us" : ""}?logo=1`}
                target="_blank"
              >
                <TruBeLogo
                  className={
                    inverse ? "tbk-text-basic-white" : "tbk-text-primary"
                  }
                />
              </a>
            </div>
          </div>
          <ul className="tb-nav-top-links tbk-ml-1 tbk-mb-0 tbk-flex tbk-min-w-0 tbk-grow tbk-items-center tbk-gap-1">
            <MenuItem
              className={`tbk-hidden${showLinks ? " md:tbk-block" : ""}`}
            >
              <NavMenu
                buttonElement={ButtonElement}
                buttonElementProps={{
                  title: "TruBe Lifestyle",
                  to: "/lifestyle",
                  isNight: inverse,
                  paddingClass: "tbk-pl-4 tbk-pr-4", // Tailwind классы
                }}
                isNight={inverse}
                bgClass="tbk-bg-basic-white"
                bgInverseClass="tbk-backdrop-blur-xl"
              >
                <div className="tbk-px-2_ tbk-pb-1.5_">
                  {(true || isLoggedIn) && (
                    <NavMenuLink
                      isNight={inverse}
                      to={"/lifestyle"}
                      title="Lifestyle Membership"
                      // onClick={onViewCoachesNavLinkClick}
                      className={`${
                        inverse ? "tbk-text-basic-white" : "tbk-text-primary"
                      } tbk-px-2 hover:tbk-text-coral`}
                    />
                  )}
                  <hr className="tbk-mx-2 tbk-my-0 tbk-border-blue-grey-light" />
                  <NavMenuLink
                    title="Upcoming Classes"
                    isNight={inverse}
                    to="/events/upcoming"
                    className={`${
                      inverse ? "tbk-text-basic-white" : "tbk-text-primary"
                    } tbk-px-2 hover:tbk-text-coral`}
                  />
                  <hr className="tbk-mx-2 tbk-my-0 tbk-border-blue-grey-light" />
                  <NavMenuLink
                    title="FAQ"
                    isNight={inverse}
                    to="/faq#lifestyle"
                    className={`${
                      inverse ? "tbk-text-basic-white" : "tbk-text-primary"
                    } tbk-px-2 hover:tbk-text-coral`}
                  />
                </div>
              </NavMenu>
            </MenuItem>

            <MenuItem
              className={`tbk-hidden${showLinks ? " md:tbk-block" : ""}`}
            >
              <NavMenu
                title="Private Coaching"
                buttonElement={ButtonElement}
                buttonElementProps={{
                  title: "Private Coaching",
                  to: "/partners",
                  isNight: inverse,
                  paddingClass: "tbk-pl-2 tbk-pr-2", // Tailwind классы
                }}
                isNight={inverse}
                bgClass="tbk-bg-basic-white"
                bgInverseClass="tbk-backdrop-blur-xl"
              >
                <div className="tbk-px-2_ tbk-pb-1.5_">
                  {(true || isLoggedIn) && (
                    <NavMenuLink
                      isNight={inverse}
                      to={"/partners"}
                      title={
                        <span>
                          Find Your{" "}
                          <FormattedMessage
                            id="Trainer"
                            defaultMessage="Trainer"
                          />
                        </span>
                      }
                      // onClick={onViewCoachesNavLinkClick}
                      className={`${
                        inverse ? "tbk-text-basic-white" : "tbk-text-primary"
                      } tbk-px-2 hover:tbk-text-coral`}
                    />
                  )}
                  <hr className="tbk-mx-2 tbk-my-0 tbk-border-blue-grey-light" />
                  <NavMenuLink
                    title="How it works"
                    isNight={inverse}
                    to="/about"
                    className={`${
                      inverse ? "tbk-text-basic-white" : "tbk-text-primary"
                    } tbk-px-2 hover:tbk-text-coral`}
                  />
                  <hr className="tbk-mx-2 tbk-my-0 tbk-border-blue-grey-light" />
                  <NavMenuLink
                    title="FAQ"
                    isNight={inverse}
                    to="/faq#private-coaching"
                    className={`${
                      inverse ? "tbk-text-basic-white" : "tbk-text-primary"
                    } tbk-px-2 hover:tbk-text-coral`}
                  />
                </div>
              </NavMenu>
            </MenuItem>

            <MenuItem
              className={`tbk-px-2 tbk-hidden${
                showLinks ? " xl:tbk-block" : ""
              }`}
            >
              <NavLink
                to="/contact"
                className={`tb-nav-link tbk-flex tbk-whitespace-nowrap ${
                  inverse ? "tbk-text-basic-white" : "tbk-text-primary"
                }`}
                target="_blank"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="tbk-mr-1 tbk-hidden tbk-align-middle"
                >
                  <path
                    d="M12.0023 3H11.9977C7.03537 3 3 7.0365 3 12C3 13.9688 3.6345 15.7935 4.71338 17.2751L3.59175 20.6186L7.05113 19.5128C8.47425 20.4555 10.1719 21 12.0023 21C16.9646 21 21 16.9624 21 12C21 7.03763 16.9646 3 12.0023 3ZM17.2391 15.7091C17.022 16.3222 16.1603 16.8307 15.4729 16.9792C15.0026 17.0794 14.3884 17.1593 12.3206 16.302C9.67575 15.2063 7.9725 12.5186 7.83975 12.3442C7.71263 12.1699 6.771 10.9211 6.771 9.62963C6.771 8.33813 7.42688 7.70925 7.69125 7.43925C7.90838 7.21763 8.26725 7.11637 8.6115 7.11637C8.72288 7.11637 8.823 7.122 8.913 7.1265C9.17737 7.13775 9.31012 7.1535 9.4845 7.57088C9.70162 8.094 10.2304 9.3855 10.2934 9.51825C10.3575 9.651 10.4216 9.831 10.3316 10.0054C10.2473 10.1854 10.173 10.2652 10.0402 10.4183C9.9075 10.5712 9.7815 10.6883 9.64875 10.8525C9.52725 10.9954 9.39 11.1484 9.543 11.4128C9.696 11.6715 10.2247 12.5344 11.0033 13.2274C12.0079 14.1217 12.8224 14.4075 13.1137 14.529C13.3309 14.619 13.5896 14.5976 13.7482 14.4289C13.9496 14.2118 14.1982 13.8518 14.4514 13.4974C14.6314 13.2431 14.8586 13.2116 15.0971 13.3016C15.3401 13.386 16.626 14.0216 16.8904 14.1532C17.1547 14.286 17.3291 14.349 17.3932 14.4604C17.4563 14.5718 17.4563 15.0949 17.2391 15.7091Z"
                    fill={inverse ? "#ffffff" : "#203b54"}
                  />
                </svg>
                Contact Us
              </NavLink>
            </MenuItem>
          </ul>

          <div className="tbk-flex tbk-items-center">
            {showRegionSelect /* && !isLoggedIn*/ ? (
              <div
                className={`tb-region-indicator tbk-mr-1 tbk-rounded-lg tbk-border tbk-border-2 lg:tbk-mr-2 ${
                  inverse
                    ? "tbk-border-basic-white"
                    : "tbk-border-grey-inactive"
                } tbk-px-1 tbk-py-1 lg:tbk-px-2`}
                role="button"
                onClick={handleRegionClick}
              >
                <div className="tbk-flex tbk-items-center">
                  <div
                    className={`tbk-text-main tbk-mr-1 !tbk-leading-none ${
                      inverse ? "tbk-text-basic-white" : "tbk-text-primary"
                    }`}
                  >
                    {region /*process.env.REACT_APP_REGION*/ === "GB"
                      ? "UK"
                      : "US"}
                  </div>
                  <Globe fill={inverse ? "#ffffff" : "#000000"} />
                </div>
              </div>
            ) : null}
            {isLoggedIn || clientWidth() < 960
              ? showMenu && <Menu isNight={inverse} />
              : showMenu && (
                  <>
                    <Link
                      to={{
                        pathname: "/login/",
                        search: "?authState=signUp",
                        state: returnTo ? { from: returnTo } : undefined,
                      }}
                      className={`tbk-text-main tbk-mr-4 ${
                        inverse ? "tbk-text-basic-white" : "tbk-text-primary"
                      } hover:tbk-text-coral-light`}
                    >
                      Login
                    </Link>
                    <Link
                      to={{
                        pathname: "/become-a-member",
                        search: "?authState=signUp",
                        state: returnTo ? { from: returnTo } : undefined,
                      }}
                      target="_blank"
                    >
                      <Button
                        variant={inverse ? "secondary" : null}
                        color={inverse ? "basic" : "coral"}
                        size="md"
                        startIcon={<UserPicOIcon />}
                      >
                        <div className="tbk-text-main tbk-pl-1">Join Now</div>
                      </Button>
                    </Link>
                  </>
                )}
          </div>
        </div>
      </div>
    </nav>
  );
};

Header.displayName = "Header";
Header.propTypes = {
  heroRef: PropTypes.object.isRequired,
};
Header.defaultProps = {
  showLinks: true,
  showMenu: true,
};

export default Header;
